import { useAffiliationAvailabilityGate } from '@/hooks/use-affiliation-availability';
import { Button, HStack, IconButton } from '@chakra-ui/react';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import { DownloadDataWindow, NavArrowDown, Plus, Upload } from 'iconoir-react';
import { parseAsString, parseAsStringEnum, useQueryState } from 'nuqs';
import React, { lazy, Suspense, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  STEP_VALUES,
  type WizardStep,
} from '@/pages/affiliate-import/_hooks/useAffiliateImportWizard';

const SendInvitationModal = lazy(
  () => import('@/components/actions/send-invitation/send-invitation-modal')
);

interface AddAffiliateButtonProps {
  isLoading?: boolean;
  programId?: string;
  isExportDisabled?: boolean;
  onOpenExport?: () => void;
}

const AddAffiliateButton: React.FC<AddAffiliateButtonProps> = ({
  isLoading = false,
  isExportDisabled = false,
  onOpenExport,
  programId,
}) => {
  const { t } = useTranslation();
  const availability = useAffiliationAvailabilityGate();
  const moreOptionsRef = useRef<HTMLButtonElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Sets the same URL keys the AffiliateImportModal listens on (`step`, `program`).
  // Both components live on the affiliates listing pages, so writing the URL state
  // here drives the modal open without prop wiring.
  const [, setImportStep] = useQueryState('step', parseAsStringEnum<WizardStep>(STEP_VALUES));
  const [, setImportProgram] = useQueryState('program', parseAsString.withDefault(''));

  const handleOpenModal = () => availability.guard(() => setIsModalOpen(true));
  const handleCloseModal = () => setIsModalOpen(false);

  const handleOpenImport = () =>
    availability.guard(
      () => {
        if (programId) {
          void setImportProgram(programId);
        }
        void setImportStep('upload');
      },
      // The menu item unmounts when selected. Return to its persistent trigger.
      () => moreOptionsRef.current
    );

  return (
    <>
      {availability.modal}
      <HStack gap={0}>
        <Button
          onClick={handleOpenModal}
          size="sm"
          gap={2}
          disabled={isLoading}
          loading={isLoading}
          roundedRight="none"
        >
          <Plus width={16} height={16} />
          {t('affiliates.addNew.button')}
        </Button>
        <MenuRoot>
          <MenuTrigger asChild>
            <IconButton
              ref={moreOptionsRef}
              size="sm"
              variant="solid"
              colorPalette="gray"
              aria-label={t('affiliates.addNew.moreOptions')}
              roundedLeft="none"
              borderLeft="1px solid"
              borderLeftColor="gray.700"
              disabled={isLoading}
            >
              <NavArrowDown width={14} height={14} />
            </IconButton>
          </MenuTrigger>
          <MenuContent>
            {onOpenExport && (
              <MenuItem
                value="export-affiliates"
                onClick={onOpenExport}
                disabled={isExportDisabled}
              >
                <DownloadDataWindow width={16} height={16} />
                {t('affiliates.export.action')}
              </MenuItem>
            )}
            <MenuItem value="import-csv" onClick={handleOpenImport}>
              <Upload width={16} height={16} />
              {t('affiliates.addNew.importCsv')}
            </MenuItem>
          </MenuContent>
        </MenuRoot>
      </HStack>

      {isModalOpen && (
        <Suspense fallback={null}>
          <SendInvitationModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            preselectedProgramId={programId}
            entryIntent="affiliate-add"
          />
        </Suspense>
      )}
    </>
  );
};

export default AddAffiliateButton;
